import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import { FileText } from 'lucide-react';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuOpsCopyHoverMenu from '@/temu-platform/components/TemuOpsCopyHoverMenu';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import useTemuTemplateScope from '@/temu-platform/hooks/useTemuTemplateScope';
import { useTemuPageShopScope } from '@/temu-platform/hooks/useTemuPageShopScope';
import TemuTemplateScopeTabs from '@/temu-platform/components/TemuTemplateScopeTabs';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { usePlatformShopPick, resolveWriteShopId } from '@/hooks/usePlatformShopPick';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { copyTemplateName } from '@/temu-platform/utils/temuTemplateCopyName';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import {
  buildTemuWorkspaceBatchOpsMenu,
  buildTemplateCopyMenuOptions,
  isTeamTemplateScope,
  resolveCopyTargetScope,
  resolveTemplateRowShopId,
  rowCanCopyScopedTemplate,
  TEMU_TEMPLATE_SCOPES,
} from '@/temu-platform/constants/temuTemplateScope';
import { showToast } from '@/utils/toast';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import CertificateBindProductModal from '@/temu-platform/modules/certificates/CertificateBindProductModal';
import CertificateLocalSaveModal from '@/temu-platform/modules/certificates/CertificateLocalSaveModal';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import '@/styles/temu-platform/temu-product-ops.css';

/** 平台 status：1 待生效、2 已生效、3 已失效、4 待填写、5 即将失效、6 草稿 */
const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: '1', label: '待生效' },
  { value: '2', label: '已生效' },
  { value: '3', label: '已失效' },
  { value: '4', label: '待填写' },
  { value: '5', label: '即将失效' },
  { value: '6', label: '草稿' },
];

const STATUS_LABELS = Object.fromEntries(
  STATUS_OPTIONS.filter((o) => o.value).map((o) => [o.value, o.label]),
);

function parseTypeOptions(res) {
  const list = res?.certificateTypeInfoList
    || res?.data?.certificateTypeInfoList
    || res?.info?.certificateTypeInfoList
    || [];
  return Array.isArray(list) ? list.filter((item) => Number(item?.certificateTypeId) !== 844) : [];
}

function typeOptionLabel(item) {
  return item?.certificateType || item?.certificateTypeName || String(item?.certificateTypeId || '');
}

function statusLabel(status, row) {
  if (row?.localTemplate && row?.canSyncToTemu) return i18n.t('certLib.pendingSync', { ns: 'workbench' });
  if (status == null || status === '') return '—';
  const statusKey = STATUS_LABELS[String(status)] ? `certLib.st${status}` : '';
  return statusKey ? i18n.t(statusKey, { ns: 'workbench', defaultValue: STATUS_LABELS[String(status)] }) : String(status);
}

function statusModifier(status) {
  const s = Number(status);
  if (s === 2) return 'success';
  if (s === 3 || s === 4) return 'failed';
  if (s === 5) return 'pending';
  return 'pending';
}

function dimensionLabel(dim) {
  if (Number(dim) === 1) return i18n.t('certLib.partialGoods', { ns: 'workbench' });
  if (Number(dim) === 2) return i18n.t('certLib.allGoods', { ns: 'workbench' });
  return '—';
}

function bindSkcLabel(row) {
  const count = Number(row?.bindSkcCount);
  if (Number.isFinite(count) && count > 0) return i18n.t('certLib.boundCount', { ns: 'workbench', count });
  if (Number(row?.bindSkcFlag) === 1) return i18n.t('certLib.bound', { ns: 'workbench' });
  if (Number(row?.bindSkcFlag) === 0) return i18n.t('certLib.unbound', { ns: 'workbench' });
  return '—';
}

function formatTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('zh-CN', { hour12: false });
}

function fileNamesOf(row) {
  const files = Array.isArray(row?.fileList) ? row.fileList : [];
  if (files.length) {
    return files.map((f) => f.fileName || f.fileUrl || i18n.t('certLib.fileFallback', { ns: 'workbench' })).filter(Boolean);
  }
  if (row?.fileName) return [row.fileName];
  return [];
}

const TABLE_COLUMNS = [
  { key: 'check', label: '', width: '50px' },
  { key: 'poolSn', label: '证书编号' },
  { key: 'type', get label() { return fixedUi("证书类型"); } },
  { key: 'files', get label() { return fixedUi("证书文件"); } },
  { key: 'status', get label() { return fixedUi("状态"); }, width: '100px' },
  { key: 'dimension', get label() { return fixedUi("适用范围"); }, width: '100px' },
  { key: 'bindSkc', label: '绑定 SKC', width: '110px' },
  { key: 'time', get label() { return fixedUi("时间"); } },
  { key: 'updatedAt', label: '平台更新' },
  { key: 'ops', get label() { return fixedUi("操作"); }, width: '220px' },
];

const COL_SPAN = TABLE_COLUMNS.length;

function rowKey(row) {
  if (row?.localTemplate) return `local-${row.id}`;
  return String(row?.poolSn || row?.id || '');
}

function FileDetailModal({ open, row, onClose }) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !row) return null;

  const files = Array.isArray(row.fileList) ? row.fileList : [];

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button type="button" className="tpo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-platform-cert-detail-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-platform-cert-detail-title">{t('certLib.fileTitle', { name: row.poolSn || row.name || '—' })}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>{t('moduleShell.close')}</button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          <p className="tpo-batch-modal__hint">
            {row.certificateTypeName || row.certificateTypeCode || '—'}
            {' · '}
            {statusLabel(row.status, row)}
            {' · '}
            {dimensionLabel(row.certificateDimension)}
          </p>
          <div className="tpo-drafts__table-wrap">
            <table className="tpo-publish__table tpo-drafts__table tpo-certs-table">
              <thead>
                <tr>
                  <th className="tpo-certs-th">{t('certLib.fileName')}</th>
                  <th className="tpo-certs-th">{t('certLib.colOps')}</th>
                </tr>
              </thead>
              <tbody>
                {files.length === 0 ? (
                  <tr><td colSpan={2} className="tpo-drafts__empty-cell">{t('certLib.noFiles')}</td></tr>
                ) : (
                  files.map((file, idx) => (
                    <tr key={`${file.fileMd5 || file.fileUrl || idx}`}>
                      <td className="tpo-certs-td">{file.fileName || '—'}</td>
                      <td className="tpo-certs-td">
                        {file.fileUrl ? (
                          <a
                            className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                            href={file.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('certLib.open')}
                          </a>
                        ) : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onClose}>{t('moduleShell.close')}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}



/**
 * 模板库 · 证书库：
 * 列表走本地证书模板；新建：同步证书类型库 → 按 needUpload 规则填写 → 店铺文件预转换后落本地。
 */
export default function CertificateLibraryPage({
  syncKinds = ['certificateLibrary', 'certificateLibraryBatchCopy'],
} = {}) {
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, pageShopScope, scopedShops = [] } = useTemuPageShopScope();
  const {
    authShopId,
    scopeTab,
    changeScopeTab,
    listShopId,
    listAllShops,
    canQuery,
    isOfficialTab,
    canAccessTeam,
  } = useTemuTemplateScope({ loadCategories: false, preferredShopId: pageShopScope });
  
  const { pickShop, shopPickModal } = usePlatformShopPick({
    shops: scopedShops,
    initialShopId: listShopId,
  });

  const ensureWriteShopId = useCallback(async ({
    title = t('productTemplate.pickShopTitle'),
    description = t('productTemplate.pickShopDesc'),
    confirmText = t('productTemplate.confirm'),
  } = {}) => resolveWriteShopId({
    listAllShops,
    pageShopScope,
    listShopId,
    pickShop,
    title,
    description,
    confirmText,
  }), [listAllShops, pageShopScope, listShopId, pickShop, t]);

const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [queryNonce, setQueryNonce] = useState(0);
  const [detailRow, setDetailRow] = useState(null);
  const [saveMode, setSaveMode] = useState(null);
  const [saveSeed, setSaveSeed] = useState(null);
  const [bindRow, setBindRow] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [batchCopying, setBatchCopying] = useState(false);
  const [moving, setMoving] = useState(false);
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();

  const loadList = useCallback(async () => {
    if (!canQuery || !listShopId) {
      setRows([]);
      setTotal(0);
      setSelectedKeys(new Set());
      return;
    }
    setLoading(true);
    try {
      const res = await temuPlatformAPI.listCertificateLibrary(listShopId, {
        source: isOfficialTab ? 'official' : 'platform',
        scope: scopeTab,
        page,
        pageSize,
        keyword: keyword || undefined,
        certificateTypeId: typeFilter || undefined,
        status: statusFilter || undefined,
        ...(listAllShops ? { allShops: true } : {}),
      });
      setRows(Array.isArray(res?.list) ? res.list : []);
      setTotal(Number(res?.total) || 0);
      // 跨页选中：翻页刷新列表时保留已选
    } catch (e) {
      setRows([]);
      setTotal(0);
      showToast(e?.message || t('certLib.loadFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [canQuery, listShopId, listAllShops, isOfficialTab, scopeTab, page, pageSize, keyword, typeFilter, statusFilter, queryNonce, t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!authShopId) {
      setTypeOptions([]);
      return;
    }
    temuPlatformAPI.listCertificateTypes(authShopId)
      .then((res) => setTypeOptions(parseTypeOptions(res)))
      .catch(() => setTypeOptions([]));
  }, [authShopId]);

  useEffect(() => {
    setKeywordDraft('');
    setKeyword('');
    setTypeFilter('');
    setStatusFilter('');
    setPage(1);
    setQueryNonce((n) => n + 1);
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    setDetailRow(null);
    setSaveMode(null);
    setSaveSeed(null);
    setBindRow(null);
    setSelectedKeys(new Set());
  }, [authShopId]);

  useEffect(() => {
    if (!authShopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopSyncStatus(authShopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const mod = modules.find((m) => String(m.module || '').toUpperCase() === 'CERTIFICATE_LIBRARY');
        const at = mod?.lastSyncEndAt || mod?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [authShopId]);

  const handleSync = useCallback(async () => {
    const writeShopId = await ensureWriteShopId({
      title: t('certLib.pickSyncShop'),
      description: t('certLib.pickSyncDesc'),
      confirmText: t('certLib.syncThisShop'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const track = await startJob({
      kind: 'certificateLibrary',
      shopId: writeShopId,
      label: t('certLib.syncUpdate'),
      silent: true,
    });
    if (!track?.id) return;
    await new Promise((resolve) => {
      const unsub = onTrackDone(track.id, async (result) => {
        unsub();
        if (result?.ok) {
          const view = result.view || {};
          const count = Number(view.imported) || 0;
          const finishedAt = view.finishedAt || new Date().toISOString();
          setLastSyncedAt(String(finishedAt));
          setPersistedSyncedAt(String(finishedAt));
          showToast(view.message || t('certLib.syncDone', { count }), 'success');
          setPage(1);
          setQueryNonce((n) => n + 1);
        }
        resolve();
      });
    });
  }, [ensureWriteShopId, startJob, onTrackDone, t]);

  const {
    refresh: onSyncLibrary,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync);

  const syncing = syncingLocal || isBusy('certificateLibrary', authShopId);
  const certLibBatchCopying = isBusy('certificateLibraryBatchCopy', listShopId) || batchCopying;

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const emptyHint = useMemo(() => {
    if (!canQuery) return t('returnList.pickShop');
    if (loading) return t('returnList.loading');
    if (isOfficialTab) return t('certLib.emptyOfficial');
    return t('certLib.emptyPullTemu');
  }, [canQuery, loading, isOfficialTab, latestSyncedAt, t]);

  const refreshAfterMutation = useCallback(() => {
    setQueryNonce((n) => n + 1);
  }, []);

  const onCopyOfficial = async (row, targetScope) => {
    if (!canQuery || !row?.id || copyingId) return;
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('certLib.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const resolvedTarget = resolveCopyTargetScope(targetScope, { isOfficialTab, scopeTab });
    setCopyingId(row.id);
    try {
      await temuPlatformAPI.copyOfficialCertificateLibrary(
        writeShopId,
        {
          sourceId: row.id,
          sourceScope: TEMU_TEMPLATE_SCOPES.official,
          targetScope: resolvedTarget,
          name: copyTemplateName(row.name),
        },
      );
      showToast(
        isTeamTemplateScope(resolvedTarget)
          ? t('certLib.copiedTeamTemu')
          : t('certLib.copiedShopTemu'),
        'success',
      );
      changeScopeTab(resolvedTarget, {
        onReset: () => {
          setPage(1);
          setQueryNonce((n) => n + 1);
        },
      });
      if (scopeTab === resolvedTarget) {
        setPage(1);
        setQueryNonce((n) => n + 1);
      }
    } catch (e) {
      showToast(e?.message || t('productTemplate.copyFailed'), 'error');
    } finally {
      setCopyingId(null);
    }
  };

  const onSyncToTemu = async (row) => {
    if (!canQuery || !row?.id || !row?.localTemplate || syncingId) return;
    const shopId = resolveTemplateRowShopId(row, listShopId);
    setSyncingId(row.id);
    try {
      const res = await temuPlatformAPI.prepareCertificateTemplateSync(shopId, row.id);
      const item = res?.item || {};
      setSaveSeed({
        ...item,
        localTemplateId: row.id,
        shopId: item.shopId || shopId,
      });
      setSaveMode('create');
      showToast(t('certLib.finishThenSaveTemu'), 'success');
    } catch (e) {
      showToast(e?.message || t('certLib.convertFailed'), 'error');
    } finally {
      setSyncingId(null);
    }
  };

  const selectableKeys = useMemo(
    () => rows.map((row) => rowKey(row)).filter(Boolean),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resolveSelectedRows = () => {
    const map = selectedRowsRef.current;
    for (const key of [...map.keys()]) {
      if (!selectedKeys.has(key)) map.delete(key);
    }
    rows.forEach((row) => {
      const key = rowKey(row);
      if (key && selectedKeys.has(key)) map.set(key, row);
    });
    return Array.from(selectedKeys).map((key) => map.get(key)).filter(Boolean);
  };

  const writeLibraryCsv = useCallback((exportRows, filename) => {
    downloadCsv(
      filename,
      ['poolSn', 'certificateType', 'files', 'status', 'dimension', 'bindSkc', 'effectiveTime', 'invalidTime', 'lastUpdateTime'],
      exportRows.map((row) => ({
        poolSn: row.poolSn || '',
        certificateType: row.certificateTypeName || row.certificateTypeCode || '',
        files: fileNamesOf(row).join('、'),
        status: statusLabel(row.status, row),
        dimension: dimensionLabel(row.certificateDimension),
        bindSkc: bindSkcLabel(row),
        effectiveTime: row.effectiveTime || '',
        invalidTime: row.invalidTime || '',
        lastUpdateTime: row.lastUpdateTime || formatTime(row.lastSyncedAt),
      })),
    );
  }, []);

  const fetchAllLibraryRows = useCallback(async () => {
    if (!canQuery || !listShopId) return [];
    return fetchAllPages(async (pageNo, size) => {
      const res = await temuPlatformAPI.listCertificateLibrary(listShopId, {
        source: isOfficialTab ? 'official' : 'platform',
        scope: scopeTab,
        page: pageNo,
        pageSize: size,
        keyword: keyword || undefined,
        certificateTypeId: typeFilter || undefined,
        status: statusFilter || undefined,
        ...(listAllShops ? { allShops: true } : {}),
      });
      return {
        items: Array.isArray(res?.list) ? res.list : [],
        total: Number(res?.total) || 0,
      };
    });
  }, [canQuery, listShopId, isOfficialTab, scopeTab, keyword, typeFilter, statusFilter, listAllShops]);

  const exportFiltered = useCallback(async () => {
    if (!canQuery) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const exportRows = await fetchAllLibraryRows();
      if (!exportRows.length) {
        showToast(t('certLib.noneExport'), 'warning');
        return;
      }
      writeLibraryCsv(exportRows, `temu-certificate-library-filtered-${Date.now()}.csv`);
      showToast(t('certLib.exported', { count: exportRows.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('tiktokCatalog.exportFilterFailed'), 'error');
    } finally {
      setExporting(false);
    }
  }, [canQuery, exporting, fetchAllLibraryRows, writeLibraryCsv, t]);

  const exportPage = useCallback(() => {
    if (!rows.length) {
      showToast(t('certLib.noneExport'), 'warning');
      return;
    }
    writeLibraryCsv(rows, `temu-certificate-library-page-${Date.now()}.csv`);
    showToast(t('certLib.exported', { count: rows.length }), 'success');
  }, [rows, writeLibraryCsv, t]);

  const exportSelected = useCallback(() => {
    const exportRows = resolveSelectedRows();
    if (!exportRows.length) {
      showToast(t('certLib.pickExport'), 'warning');
      return;
    }
    writeLibraryCsv(exportRows, `temu-certificate-library-selected-${Date.now()}.csv`);
    showToast(t('certLib.exportedSelected', { count: exportRows.length }), 'success');
  }, [selectedKeys, writeLibraryCsv, t]);

  const onBatchDelete = async () => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('certLib.pickDelete'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .filter((row) => !row?.localTemplate)
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('certLib.missingLocalId'), 'error');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('returnList.batchDelete'),
      message: t('certLib.batchDeleteMsg', { count: ids.length, platform: 'TEMU' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const grouped = new Map();
      resolveSelectedRows().forEach((row) => {
        if (row?.localTemplate) return;
        const id = Number(row?.id);
        if (!Number.isFinite(id) || id <= 0) return;
        const sid = resolveTemplateRowShopId(row, listShopId);
        if (!sid) return;
        if (!grouped.has(sid)) grouped.set(sid, []);
        grouped.get(sid).push(id);
      });
      let deleted = 0;
      for (const [sid, certIds] of grouped.entries()) {
        const res = await temuPlatformAPI.batchDeleteCertificateLibrary(sid, certIds);
        const n = Number(res?.deleted);
        deleted += Number.isFinite(n) && n >= 0 ? n : certIds.length;
      }
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : ids.length;
      if (count > 0) {
        showToast(t('certLib.deletedLocal', { count }), 'success');
      } else {
        showToast(t('certLib.noneDeleted'), 'warning');
      }
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await loadList();
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const onBatchCopy = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('certLib.pickOfficialCopy'), 'warning');
      return;
    }
    const writeShopId = await ensureWriteShopId({
      title: t('productTemplate.copyPickTitle'),
      description: t('certLib.copyPickDesc'),
      confirmText: t('productTemplate.copyHere'),
    });
    if (!writeShopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('productTemplate.missingIdCopy'), 'error');
      return;
    }
    setBatchCopying(true);
    try {
      const track = await startJob({
        kind: 'certificateLibraryBatchCopy',
        shopId: writeShopId,
        label: t('certLib.copyJob', { count: ids.length }),
        silent: true,
        payload: {
          ids,
          sourceScope: TEMU_TEMPLATE_SCOPES.official,
          targetScope,
        },
      });
      if (!track?.id) return;
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (result) => {
          unsub();
          const view = result?.view || {};
          const copied = Number(view.imported ?? view.copied) || 0;
          if (result?.ok) {
            showToast(
              view.message
                || t('certLib.copiedTo', { count: copied || ids.length, scope: isTeamTemplateScope(targetScope) ? t('certLib.scopeTeam') : t('certLib.scopeShop') }),
              'success',
            );
            changeScopeTab(targetScope, {
              onReset: () => {
                setPage(1);
                setQueryNonce((n) => n + 1);
              },
            });
            if (scopeTab === targetScope) {
              setPage(1);
              setQueryNonce((n) => n + 1);
            }
          } else {
            showToast(view?.error || view?.message || t('productTemplate.batchCopyFailed'), 'error');
          }
          resolve();
        });
      });
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchCopyFailed'), 'error');
    } finally {
      setBatchCopying(false);
    }
  };

  const onBatchMove = async (targetScope) => {
    if (!canQuery || selectedKeys.size === 0) {
      showToast(t('certLib.pickMove'), 'warning');
      return;
    }
    const ids = resolveSelectedRows()
      .filter((row) => row?.localTemplate)
      .map((row) => Number(row?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!ids.length) {
      showToast(t('certLib.moveLocalOnly'), 'error');
      return;
    }
    setMoving(true);
    try {
      const grouped = new Map();
      resolveSelectedRows().forEach((row) => {
        if (!row?.localTemplate) return;
        const id = Number(row?.id);
        if (!Number.isFinite(id) || id <= 0) return;
        const sid = resolveTemplateRowShopId(row, listShopId);
        if (!sid) return;
        if (!grouped.has(sid)) grouped.set(sid, []);
        grouped.get(sid).push(id);
      });
      let moved = 0;
      for (const [sid, certIds] of grouped.entries()) {
        const res = await temuPlatformAPI.moveCertificateLibraryTemplates(sid, {
          ids: certIds,
          targetScope,
        });
        const n = Number(res?.moved);
        moved += Number.isFinite(n) && n >= 0 ? n : certIds.length;
      }
      showToast(t('productTemplate.movedCount', { count: moved }), 'success');
      setSelectedKeys(new Set());
      selectedRowsRef.current.clear();
      setPage(1);
      setQueryNonce((n) => n + 1);
    } catch (e) {
      showToast(e?.message || t('productTemplate.batchMoveFailed'), 'error');
    } finally {
      setMoving(false);
    }
  };

  return (
    <>
      <TemuSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || deleting || certLibBatchCopying || moving || exporting}
            batchCopying={certLibBatchCopying}
            batchMoving={moving}
            batchOpsMenu={buildTemuWorkspaceBatchOpsMenu({
              scopeTab,
              isOfficialTab,
              canAccessTeam,
              selectedCount: selectedKeys.size,
              onCopy: onBatchCopy,
              onMove: onBatchMove,
              onDelete: onBatchDelete,
              deleteDisabled: !canQuery || deleting || isOfficialTab,
            })}
            syncKinds={syncKinds}
            quotaKind="certificate-library"
            onSync={() => {
              onSyncLibrary();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!canQuery || syncing || isOfficialTab}
            lastSyncedLabel={isOfficialTab ? '' : latestSyncedLabel}
            syncHint={t('certLib.syncHintTemu')}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!canQuery || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            extraActions={isOfficialTab ? [] : [
              {
                key: 'create',
                label: t('certLib.create'),
                tone: 'primary',
                disabled: !canQuery,
                onClick: async () => {
                  const writeShopId = await ensureWriteShopId({
                    title: t('productTemplate.createPickTitle'),
                    description: t('certLib.createPickDescLocal'),
                    confirmText: t('productTemplate.createHere'),
                  });
                  if (!writeShopId) {
                    showToast(t('returnList.pickShop'), 'warning');
                    return;
                  }
                  setSaveSeed({ shopId: writeShopId });
                  setSaveMode('create');
                },
              },
            ]}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline">
              <span className="tpo-field__label">{t('certLib.keyword')}</span>
              <input
                className="tpo-input"
                value={keywordDraft}
                disabled={!canQuery}
                placeholder={t('certLib.keywordPh')}
                onChange={(e) => setKeywordDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setKeyword(keywordDraft.trim());
                    setPage(1);
                  }
                }}
              />
            </div>
            <div className="tpo-field tpo-field--inline">
              <span className="tpo-field__label">{t('certLib.colType')}</span>
              <select
                className="tpo-select"
                value={typeFilter}
                disabled={!canQuery || loading}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">{t('certLib.allTypes')}</option>
                {typeOptions.map((item) => (
                  <option key={item.certificateTypeId} value={String(item.certificateTypeId)}>
                    {typeOptionLabel(item)}
                  </option>
                ))}
              </select>
            </div>
            <div className="tpo-field tpo-field--inline">
              <span className="tpo-field__label">{t('certLib.colStatus')}</span>
              <select
                className="tpo-select"
                value={statusFilter}
                disabled={!canQuery || loading}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.value ? t('certLib.st' + opt.value) : t('certLib.statusAll')}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--ghost tpo-btn--sm"
              disabled={!canQuery || loading}
              onClick={() => {
                setKeywordDraft('');
                setKeyword('');
                setTypeFilter('');
                setStatusFilter('');
                setPage(1);
                setQueryNonce((n) => n + 1);
              }}
            >
              {t('returnList.reset')}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!canQuery || loading}
              onClick={() => {
                if (canQuery) setLoading(true);
                setKeyword(keywordDraft.trim());
                setPage(1);
                setQueryNonce((n) => n + 1);
              }}
            >
              {t('returnList.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={shopDimensionTabs}
        loading={Boolean(canQuery) && loading}
        loadingLabel={t('certLib.querying')}>
        <TemuTemplateScopeTabs
          scopeTab={scopeTab}
          canAccessTeam={canAccessTeam}
          disabled={!canQuery || loading}
          ariaLabel={t('certLib.scopeAria')}
          onChange={(tabId) => {
            changeScopeTab(tabId, {
              onReset: () => {
                setPage(1);
                setSelectedKeys(new Set());
                selectedRowsRef.current.clear();
                setQueryNonce((n) => n + 1);
              },
            });
          }}
        />
        <div className="tpo-drafts__table-wrap tpo-certs-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-certs-table tpo-cert-lib-table">
            <colgroup>
              {TABLE_COLUMNS.map((col) => (
                <col
                  key={col.key}
                  className={`tpo-certs-col tpo-certs-col--${col.key}`}
                  style={col.width ? { width: col.width } : undefined}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-certs-th tpo-certs-th--check"
                  onClick={() => {
                    if (!canQuery || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t('certLib.selectAll')}
                    disabled={!canQuery || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {TABLE_COLUMNS.filter((c) => c.key !== 'check').map((col) => (
                  <th
                    key={col.key}
                    scope="col"
                    className={`tpo-certs-th tpo-certs-th--${col.key}`}
                  >
                    {col.key === 'poolSn' ? (
                      <span className="tpo-certs-th__with-icon">
                        <FileText size={14} className="tpo-certs-th__pdf" aria-hidden="true" />
                        {i18n.t({ poolSn: 'certLib.colNo', type: 'certLib.colType', files: 'certLib.colFiles', source: 'certLib.colSource', status: 'certLib.colStatus', dimension: 'certLib.colScope', bindSkc: 'certLib.colBind', time: 'certLib.colTime', updatedAt: 'certLib.colUpdated', ops: 'certLib.colOps' }[col.key], { ns: 'workbench', defaultValue: col.label })}
                      </span>
                    ) : col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!canQuery || loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={COL_SPAN} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">{emptyHint}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  const names = fileNamesOf(row);
                  const isLocal = Boolean(row.localTemplate);
                  return (
                    <tr
                      key={key}
                      className="tpo-row--clickable"
                      onClick={(e) => {
                        if (e.target.closest('button, a, input, .tpo-copy-hover')) return;
                        toggleOne(key);
                      }}
                    >
                      <td className="tpo-certs-td tpo-certs-td--check">
                        <input
                          type="checkbox"
                          aria-label={t('returnList.selectRow', { title: row.poolSn || row.name || key })}
                          checked={selectedKeys.has(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleOne(key)}
                        />
                      </td>
                      <td className="tpo-certs-td" title={row.poolSn || row.name || ''}>
                        <strong>{row.poolSn || row.name || '—'}</strong>
                      </td>
                      <td className="tpo-certs-td" title={row.certificateTypeName || ''}>
                        {row.certificateTypeName || row.certificateTypeCode || '—'}
                      </td>
                      <td className="tpo-certs-td" title={names.join('、')}>
                        {names.length === 0
                          ? '—'
                          : names.length === 1
                            ? names[0]
                            : t('certLib.moreFiles', { name: names[0], count: names.length })}
                      </td>
                      <td className="tpo-certs-td">
                        <span className={`tpo-records__status tpo-records__status--${statusModifier(row.status)}`}>
                          {statusLabel(row.status, row)}
                        </span>
                      </td>
                      <td className="tpo-certs-td">{dimensionLabel(row.certificateDimension)}</td>
                      <td className="tpo-certs-td">{bindSkcLabel(row)}</td>
                      <td className="tpo-certs-td tpo-certs-td--time">
                        <div className="tpo-certs-time">
                          <div className="tpo-certs-time__row">
                            <span className="tpo-certs-time__label">{t('certLib.effective')}</span>
                            <span className="tpo-certs-time__value">{row.effectiveTime || '—'}</span>
                          </div>
                          <div className="tpo-certs-time__row">
                            <span className="tpo-certs-time__label">{t('certLib.invalid')}</span>
                            <span className="tpo-certs-time__value">{row.invalidTime || '—'}</span>
                          </div>
                        </div>
                      </td>
                      <td className="tpo-certs-td">{row.lastUpdateTime || formatTime(row.lastSyncedAt || row.updatedAt)}</td>
                      <td className="tpo-certs-td tpo-certs-td--ops tpo-certs-td--actions">
                        <button
                          type="button"
                          className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                          onClick={() => setDetailRow(row)}
                        >
                          {t('certLib.filesBtn')}
                        </button>
                        {isOfficialTab && rowCanCopyScopedTemplate(row) ? (
                          <TemuOpsCopyHoverMenu
                            disabled={copyingId != null}
                            busy={copyingId === row.id}
                            options={buildTemplateCopyMenuOptions({
                              scopeTab,
                              isOfficialTab,
                              canAccessTeam,
                              onCopy: (target) => onCopyOfficial(row, target),
                            })}
                          />
                        ) : null}
                        {isLocal && row.canSyncToTemu ? (
                          <button
                            type="button"
                            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                            disabled={syncingId != null}
                            onClick={() => onSyncToTemu(row)}
                          >
                            {syncingId === row.id ? t('productAction.syncing') : t('certLib.syncBackend')}
                          </button>
                        ) : null}
                        {!isOfficialTab && !isLocal ? (
                          <button
                            type="button"
                            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                            onClick={() => {
                              setSaveSeed(row);
                              setSaveMode('edit');
                            }}
                          >
                            {t('productTemplate.edit')}
                          </button>
                        ) : null}
                        {!isOfficialTab && !isLocal && Number(row.certificateDimension) === 1 ? (
                          <button
                            type="button"
                            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                            onClick={() => setBindRow(row)}
                          >
                            {t('certLib.bind')}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!canQuery}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t('certLib.pager')}
        />
      </ProductListCard>

      <FileDetailModal
        open={Boolean(detailRow)}
        row={detailRow}
        onClose={() => setDetailRow(null)}
      />
      <CertificateLocalSaveModal
        open={Boolean(saveMode)}
        mode={saveMode || 'create'}
        shopId={resolveTemplateRowShopId(saveSeed, authShopId)}
        scope={scopeTab}
        typeOptions={typeOptions}
        seed={saveSeed}
        onClose={() => {
          setSaveMode(null);
          setSaveSeed(null);
        }}
        onDone={() => {
          refreshAfterMutation();
        }}
      />
      <CertificateBindProductModal
        open={Boolean(bindRow)}
        shopId={resolveTemplateRowShopId(bindRow, authShopId)}
        row={bindRow}
        onClose={() => setBindRow(null)}
        onDone={refreshAfterMutation}
      />
      {shopPickModal}
    </>
  );
}
